import * as React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Collapse,
    Divider,
    IconButton,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import {useTranslation} from "react-i18next";
import {ApprovedBranch, JobPosition} from "../../../../hooks/useJobPositions";
import JobStatusChip from "../JobStatusChip";
import JobPositionDetails from "../JobPositionDetails";
import {JobAssignmentListProps} from "../JobAssignmentList";

const PAGE_STEP = 3;

export interface JobPositionsMobileProps {
    loading: boolean;
    positions: JobPosition[];
    branches: ApprovedBranch[];
    /** All assignments; each card filters by position name. */
    assignments: JobPosition[];
    assignmentLoading: boolean;
    assignmentProps: Omit<JobAssignmentListProps, "assignments" | "loading">;
    onEdit: (row: JobPosition) => void;
    onAssign: (branchId: string) => void;
    onApprove: (id: string) => void;
    onReject: (id: string) => void;
    onClose: (id: string) => void;
}

/** Card list: tap a card to expand its branch distribution, "Show more" reveals further positions. */
export default function JobPositionsMobile({
    loading,
    positions,
    branches,
    assignments,
    assignmentLoading,
    assignmentProps,
    onEdit,
    onAssign,
    onApprove,
    onReject,
    onClose,
}: Readonly<JobPositionsMobileProps>): React.ReactElement {
    const {t} = useTranslation();
    const [expandedId, setExpandedId] = React.useState<string | null>(null);
    const [visible, setVisible] = React.useState<number>(PAGE_STEP);

    // A new search/filter result set starts collapsed again.
    React.useEffect(() => {
        setVisible(PAGE_STEP);
    }, [positions.length]);

    if (loading) {
        return (
            <Stack spacing={2}>
                {Array.from({length: 3}).map((_, i) => (
                    <Card key={`job-skeleton-${i}`} variant="outlined" sx={{border: "1px solid", borderColor: "divider"}}>
                        <CardContent><Skeleton variant="text" width="60%"/><Skeleton variant="text" width="40%"/></CardContent>
                    </Card>
                ))}
            </Stack>
        );
    }

    if (positions.length === 0) {
        return (
            <Box sx={{textAlign: "center", py: 4}}>
                <WorkOutlineIcon sx={{fontSize: 40, color: "text.disabled", mb: 1}}/>
                <Typography variant="body2" color="text.secondary">{t("hr.jobs.listing.empty")}</Typography>
            </Box>
        );
    }

    const shown = positions.slice(0, visible);

    return (
        <Stack spacing={2}>
            {shown.map((row) => {
                const rowAssignments = assignments.filter((a) => a.name === row.name);
                const expanded = expandedId === row.id;
                return (
                    <Card key={row.id} variant="outlined" sx={{border: "1px solid", borderColor: "divider"}}>
                        <CardContent
                            onClick={() => setExpandedId(expanded ? null : row.id)}
                            sx={{cursor: "pointer", transition: "all 0.2s ease", "&:active": {transform: "scale(0.99)"}}}
                            aria-label={t("hr.jobs.mobile.ariaJobPosition", {name: row.name})}
                        >
                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <Typography variant="subtitle1" fontWeight={700} noWrap>{row.name}</Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {t("hr.jobs.mobile.branchCount", {count: rowAssignments.length})}
                                    </Typography>
                                </Box>
                                <JobStatusChip row={row}/>
                                <IconButton size="small" aria-label="expand" tabIndex={-1}
                                            sx={{transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s"}}>
                                    <ExpandMoreIcon/>
                                </IconButton>
                            </Box>
                        </CardContent>
                        <Collapse in={expanded} unmountOnExit>
                            <Divider/>
                            <CardContent>
                                <JobPositionDetails
                                    position={row}
                                    branches={branches}
                                    assignmentProps={{
                                        ...assignmentProps,
                                        assignments: rowAssignments,
                                        loading: assignmentLoading,
                                    }}
                                    onEdit={onEdit}
                                    onAssign={onAssign}
                                    onApprove={onApprove}
                                    onReject={onReject}
                                    onClose={onClose}
                                />
                            </CardContent>
                        </Collapse>
                    </Card>
                );
            })}

            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                <Typography variant="body2" color="text.secondary">
                    {t("hr.jobs.mobile.showing", {shown: shown.length, total: positions.length})}
                </Typography>
                {visible < positions.length && (
                    <Button variant="outlined" color="inherit" endIcon={<ExpandMoreIcon/>}
                            onClick={() => setVisible((v) => v + PAGE_STEP)}>
                        {t("hr.jobs.mobile.showMore")}
                    </Button>
                )}
            </Box>
        </Stack>
    );
}
